// Dates, times, numbers and money in the active locale (#1063). Every helper
// reads the locale at call time, so pure functions (view models, toasts) need
// no locale argument; a component re-renders on a switch through useLocale()
// / useT(). Time zones are the caller's, exactly as before.
//
// Replace a hard-coded 'en-US' with these (the Intl sweep, #1129).

import { getIntlLocale } from './runtime'

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>()
function cached<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>(kind: string, opts: object, make: (tag: string) => T): T {
  const tag = getIntlLocale()
  const key = `${kind}|${tag}|${JSON.stringify(opts)}`
  let f = cache.get(key) as T | undefined
  if (!f) cache.set(key, (f = make(tag)))
  return f
}

export function fmtNumber(n: number, opts: Intl.NumberFormatOptions = {}): string {
  return cached('n', opts, (tag) => new Intl.NumberFormat(tag, opts)).format(n)
}

/** 0.75 → "75%" (fr "75 %"). */
export function fmtPercent(share: number, opts: Intl.NumberFormatOptions = {}): string {
  return fmtNumber(share, { style: 'percent', maximumFractionDigits: 0, ...opts })
}

/** Cents → "$319.04" (fr "319,04 $US"). `hideZeroCents` drops ".00". */
export function fmtMoney(cents: number, currency = 'USD', opts: { hideZeroCents?: boolean } = {}): string {
  const whole = opts.hideZeroCents && cents % 100 === 0
  return fmtNumber(cents / 100, { style: 'currency', currency, ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}) })
}

type DateInput = string | number | Date

export function fmtDate(value: DateInput, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  return cached('d', opts, (tag) => new Intl.DateTimeFormat(tag, opts)).format(new Date(value))
}

/**
 * A date range in one format: "Oct 4 – 10", "Sep 27 – Oct 3" (fr "4–10 oct.").
 * ICU puts thin spaces around the dash; plain spaces read the same and keep
 * English exactly as the hand-built ranges were.
 */
export function fmtDateRange(from: DateInput, to: DateInput, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  const f = cached('d', opts, (tag) => new Intl.DateTimeFormat(tag, opts))
  return f.formatRange(new Date(from), new Date(to)).replace(/\u2009/g, ' ')
}

/** "7:30 PM" / "19 h 30" in the given zone. */
export function fmtTime(value: DateInput, timeZone?: string): string {
  return fmtDate(value, { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
}

/** "Sat, Oct 10" / "sam. 10 oct." in the given zone. */
export function fmtDay(value: DateInput, timeZone?: string): string {
  return fmtDate(value, { weekday: 'short', month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) })
}

/** "in 3 hours" / "dans 3 heures". */
export function fmtRelative(amount: number, unit: Intl.RelativeTimeFormatUnit, opts: Intl.RelativeTimeFormatOptions = { numeric: 'auto' }): string {
  return cached('r', opts, (tag) => new Intl.RelativeTimeFormat(tag, opts)).format(amount, unit)
}
